import { axios } from "@client-shared/services/api";
import { AxiosError, Vendor } from "@shared/index";
import { AxiosRequestConfig, isAxiosError } from "axios";
import { vendorLocalStorageKeys } from "../vendor/types";
import { TokenRefreshCancelledError } from "./token-refresh-cancelled-error";
import { BEARER_TOKEN_PREFIX, sfdcLocalStorageKeys } from "./types";

// ====================================================================================================================
// helper functions ----------------------------------//
// ====================================================================================================================

// set the request interceptor
const setRequestInterceptor = () => {
  return axios.interceptors.request.use((request) => {
    const idToken = localStorage.getItem(sfdcLocalStorageKeys.sfdcIdToken);
    if (idToken) {
      request.headers.Authorization = `${BEARER_TOKEN_PREFIX}${idToken}`;
    }

    const vendorKey = getVendorKey();
    if (vendorKey) {
      request.headers.vendorKey = vendorKey;
    }

    if (process.env.REACT_APP_LOCAL?.toLowerCase() === "true" && !request._retry && vendorKey) {
      request.params = { ...request.params, vendorKey };
    }
    return request;
  });
};

let isFetchingToken = false;

// set the response interceptor
const setResponseInterceptor = (handleError: () => void, handleRefreshToken: (token: string) => Promise<void>) => {
  return axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // Requests that opt out (e.g. the Roaster Admin roaster-list endpoint, which
      // legitimately 403s when no vendor is selected) must not trigger the
      // auth-error→logout pipeline. Let the 403 fall through to the caller instead.
      const skipAuthLogout = isAxiosError(error) && (error.config as AxiosRequestConfig)?.skipAuthLogout;
      if (isAxiosError(error) && error.response?.status === 403 && !skipAuthLogout) {
        return await handleRequestError(error as AxiosError, handleError, handleRefreshToken);
      }
      let toReject: Error;

      if (isAxiosError(error)) {
        toReject = new Error(error.message);
        Object.assign(toReject, error); // Preserve AxiosError props (e.g., config, code, response)
      } else if (error instanceof Error) {
        toReject = error;
      } else {
        toReject = new Error(String(error)); // Fallback for unknown errors
      }
      throw toReject;
    }
  );
};

// A Roaster Admin operates without a vendor selected (e.g. mid "switch roaster",
// clearVendorState() removes the vendor from localStorage). Vendor-scoped requests
// then go out without a vendorKey header and the authorizer 403s. That is an
// authorization-scope condition, not a dead session — a token refresh cannot fix it,
// so the retry 403s again and trips the logout path. As long as the admin's own auth
// (id token) is still present, surface the 403 to the caller instead of logging out.
const shouldSkipAdminLogout = () => {
  const isRoasterAdmin = localStorage.getItem(sfdcLocalStorageKeys.sfdcIsRoasterAdmin) === "true";
  const hasValidAuth = Boolean(localStorage.getItem(sfdcLocalStorageKeys.sfdcIdToken));
  return isRoasterAdmin && hasValidAuth;
};

const handleRequestError = async (error: AxiosError, handleError: () => void, handleRefreshToken: (token: string) => Promise<void>) => {
  const originalRequest = error.config as AxiosRequestConfig;
  const refreshToken = localStorage.getItem(sfdcLocalStorageKeys.sfdcRefreshToken);

  // If there's no refresh token or the request has already been retried, handle the error
  if (!refreshToken || originalRequest?._retry) {
    // Don't kill an admin's session over a vendor-scoped 403 (e.g. roaster switching).
    if (shouldSkipAdminLogout()) {
      throw error;
    }
    handleError();
    return;
  }
  originalRequest._retry = true;

  // If a token is being fetched, queue the request
  if (isFetchingToken) {
    return queueFailedRequest(originalRequest);
  }

  // Handle token refresh and retry the original request
  return await attemptTokenRefreshAndRetry(originalRequest, refreshToken, handleRefreshToken, handleError);
};

const queueFailedRequest = (originalRequest: AxiosRequestConfig) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({
      resolve: () => {
        resolve(axios.request(originalRequest));
      },
      reject,
    });
  });
};

const attemptTokenRefreshAndRetry = async (
  originalRequest: AxiosRequestConfig,
  refreshToken: string,
  handleRefreshToken: (token: string) => Promise<void>,
  handleError: () => void
) => {
  isFetchingToken = true;
  try {
    await handleRefreshToken(refreshToken);
    processQueue();
    return axios.request(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError as Error);
    // A refresh cancelled by logout is already navigating away, so don't trigger a second logout.
    if (!(refreshError instanceof TokenRefreshCancelledError)) {
      handleError();
    }

    let toReject: Error;

    if (isAxiosError(refreshError)) {
      // Create a new Error that wraps the AxiosError but is still an instance of Error
      toReject = new Error(refreshError.message);
      Object.assign(toReject, refreshError); // preserve original AxiosError properties
    } else {
      toReject = refreshError as Error;
    }

    throw toReject;
  } finally {
    isFetchingToken = false;
  }
};

const ejectRequestInterceptor = (requestInterceptor: number) => {
  axios.interceptors.request.eject(requestInterceptor);
};

const ejectResponseInterceptor = (responseInterceptor: number) => {
  axios.interceptors.response.eject(responseInterceptor);
};

const failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error?: Error | null) => {
  while (failedQueue.length > 0) {
    const nextPromise = failedQueue.shift();
    if (error) {
      nextPromise?.reject(error);
    } else {
      nextPromise?.resolve();
    }
  }
};

const getVendorKey = () => {
  const vendorString = localStorage.getItem(vendorLocalStorageKeys.vendor);
  const vendor: Vendor | undefined = vendorString ? JSON.parse(vendorString) : undefined;
  return vendor?.vendorKey;
};

// ====================================================================================================================
// UseAxiosInterceptor hook ----------------------------------//
// ====================================================================================================================

export type SetAllInterceptors = (
  handleSetRequestInterceptor: (set: () => number) => void,
  handleSetResponseInterceptor: (set: (handleError: () => void, refreshToken: (token: string) => Promise<void>) => number) => void
) => void;

export type EjectAllInterceptors = (requestInterceptor: number | null, responseInterceptor: number | null) => void;

const useSfdcAuthInterceptor = () => {
  const handleSetAllInterceptors: SetAllInterceptors = (handleSetRequestInterceptor, handleSetResponseInterceptor) => {
    handleSetRequestInterceptor(setRequestInterceptor);
    handleSetResponseInterceptor(setResponseInterceptor);
  };

  const handleEjectAllInterceptors: EjectAllInterceptors = (requestInterceptor, responseInterceptor) => {
    if (requestInterceptor) {
      ejectRequestInterceptor(requestInterceptor);
    }
    if (responseInterceptor) {
      ejectResponseInterceptor(responseInterceptor);
    }
  };

  return { handleSetAllInterceptors, handleEjectAllInterceptors };
};

export default useSfdcAuthInterceptor;
